import * as THREE from 'three';
import { commitInstances } from '../gpu';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { walkHeight } from '../../sim/crew';
import type { CrewMember, Stance } from '../../sim/crew';
import type { Vessel, Faction } from '../../sim/vessel';
import { patchMaterial } from '../atmosphere';
import { primitives, type ArtSet } from './art';

// The men, drawn as instanced jointed figures: every body part is one
// instanced mesh for the whole sea, posed on the CPU each frame from the
// crew sim's stance and animation phase. Figures keep human size whatever
// the ship's class scale.
//
// Close to the camera they're the Blender-built sailors (art/blender/crew.py):
// each segment comes in several cuts (shirts, waistcoats, jackets, coats;
// hats; hair; breeches or trousers; bare feet, shoes or boots) and is coloured
// per man through three colour channels. Further off, and whenever the
// models aren't loaded, they're the simple capsule figures.

const CAPACITY = 900;
/** Beyond this the capsule figures stand in for the detailed ones (m). */
const DETAIL_RANGE = 48;

type PartName = 'torso' | 'head' | 'hat' | 'upperL' | 'upperR' | 'foreL' | 'foreR' | 'thighL' | 'thighR' | 'shinL' | 'shinR' | 'musket';

interface Pose {
  /** Pelvis height above the feet. */
  hip: number;
  /** Torso pitch forward (rad). */
  lean: number;
  /** Shoulder swing forward, abduction, elbow bend (per arm). */
  armL: [number, number, number];
  armR: [number, number, number];
  /** Hip swing forward, knee bend (per leg). */
  legL: [number, number];
  legR: [number, number];
  /** Whole body lying: 0 upright, 1 face down, -1 on the back, 2 on the side. */
  lying: number;
  musket: boolean;
}

const SKIN = [0x8d5a3b, 0xc68863, 0xe0ac8a, 0x6b4028, 0xa8734f, 0x4a2c1c].map((c) => new THREE.Color(c));
const HAIR = [0x1a1410, 0x2a1a10, 0x3b2616, 0x5a2a14, 0x8a7040, 0x6e6a64, 0x120e0c].map((c) => new THREE.Color(c));

/** Colours for the capsule figures. */
interface Outfit {
  coat: THREE.Color;
  sleeve: THREE.Color | null;
  legs: THREE.Color;
  hat: THREE.Color | null;
}

/** A man's clothes: the cut of each segment and its colour channels. */
interface Dress {
  torso: string;
  head: string;
  hat: string | null;
  upper: string;
  fore: string;
  thigh: string;
  shin: string;
  /** Channel colours [A, B, C] per segment group. */
  body: [THREE.Color, THREE.Color, THREE.Color];
  arms: [THREE.Color, THREE.Color, THREE.Color];
  legs: [THREE.Color, THREE.Color, THREE.Color];
  headC: [THREE.Color, THREE.Color, THREE.Color];
  hatC: [THREE.Color, THREE.Color, THREE.Color];
  outfit: Outfit;
}

function pick<T>(list: readonly T[], u: number): T {
  return list[Math.floor(u * list.length) % list.length]!;
}

const C = (hex: number) => new THREE.Color(hex);
const frac = (x: number) => x - Math.floor(x);

/** How a man of this faction and rank dresses (u: his appearance, 0..1). */
function dress(faction: Faction, role: CrewMember['role'], u: number, skin: THREE.Color): Dress {
  const r = (k: number) => frac(Math.sin(u * 91.7 + k * 17.3) * 43758.5);
  const hair = pick(HAIR, r(1));
  let torso: string, hat: string | null, head: string, thigh = 'trousers', shin: string;
  let coat: THREE.Color, shirt: THREE.Color, legs: THREE.Color, hose = C(0xe6e0d2), hatA: THREE.Color = C(0x141210), trim = C(0x1a1a1a);
  const beardy = faction === 'navy' ? 0.08 : faction === 'merchant' ? 0.3 : 0.5;
  head = r(2) < beardy && role !== 'boy' ? 'beard' : r(3) < 0.45 ? 'queue' : r(3) < 0.85 || role === 'boy' ? 'short' : 'bald';
  if (faction === 'navy') {
    if (role === 'officer') {
      torso = 'coat'; coat = C(0x151c38); shirt = C(0xe8e2d2); legs = C(0xe0dac8); thigh = 'breeches';
      shin = r(4) < 0.35 ? 'boots' : 'stockings'; hat = 'bicorne'; trim = C(0x101010); head = r(5) < 0.5 ? 'queue' : 'short';
    } else if (role === 'marine') {
      torso = 'coat'; coat = C(0x8a1510); shirt = C(0xe8e2d2); legs = C(0xe0dac8); thigh = 'breeches';
      shin = 'stockings'; hat = r(4) < 0.6 ? 'round' : 'tricorne'; trim = C(0xe8e2d2); head = 'queue';
    } else {
      torso = r(4) < 0.45 ? 'jacket' : r(4) < 0.8 ? 'shirt' : 'vest';
      coat = torso === 'jacket' ? C(0x243058) : pick([C(0xd8d2c2), C(0xc8c0ae), C(0xe4ddcc)], r(5));
      shirt = torso === 'shirt' ? pick([C(0x1a1a1a), C(0x8a1a12), C(0x243058)], r(6)) : C(0xd8d2c2);
      legs = pick([C(0xd6d0c0), C(0xcfc8b6), C(0x2a3050)], r(7));
      shin = r(8) < 0.55 ? 'bare' : 'shoes';
      hat = r(9) < 0.45 ? 'round' : r(9) < 0.65 ? 'knit' : null;
      hatA = hat === 'knit' ? pick([C(0x7a1812), C(0x2a3050)], r(10)) : C(0x141210);
      trim = C(0x1a1a1a);
    }
  } else if (faction === 'merchant') {
    torso = role === 'officer' ? 'jacket' : r(4) < 0.45 ? 'shirt' : r(4) < 0.8 ? 'vest' : 'jacket';
    coat = pick([C(0x6b5a44), C(0x807565), C(0x4e4538), C(0xb8ad96), C(0x3a3a44)], r(5));
    shirt = pick([C(0xd6ceb8), C(0xb8ad96), C(0x8a7a60)], r(6));
    if (torso === 'shirt') coat = shirt.clone();
    legs = pick([C(0x4a4236), C(0x6a604e), C(0x8a8068), C(0x303848)], r(7));
    shin = role === 'officer' ? 'shoes' : r(8) < 0.5 ? 'bare' : 'shoes';
    hat = role === 'officer' ? 'tricorne' : r(9) < 0.4 ? 'knit' : r(9) < 0.6 ? 'round' : null;
    hatA = hat === 'knit' ? pick([C(0x5a3a20), C(0x6a1a12), C(0x3a3a3a)], r(10)) : C(0x2a2218);
    trim = C(0x3a2a1a);
  } else if (role === 'officer') {
    // Pirate officers: a good coat off some captain's back, boots, a cocked hat.
    torso = 'coat'; coat = pick([C(0x5a0e0e), C(0x14281a), C(0x1c1c1c), C(0x2a1a3a)], r(5)); shirt = pick([C(0xd8ccaa), C(0x8a6a30)], r(6));
    legs = pick([C(0x2a241c), C(0x3a2a1e)], r(7)); thigh = r(8) < 0.5 ? 'breeches' : 'trousers'; shin = 'boots';
    hat = 'tricorne'; hatA = C(0x0e0d0c); trim = C(0xa08040); head = r(9) < 0.6 ? 'beard' : 'short';
  } else {
    // Pirates (and our own people): whatever came off the last prize.
    torso = r(4) < 0.35 ? 'shirt' : r(4) < 0.62 ? 'vest' : r(4) < 0.82 ? 'bare' : 'jacket';
    coat = pick([C(0xcfc6b0), C(0x8e2418), C(0x3d5a3a), C(0x2f3a5a), C(0x6e4a2a), C(0xd8d0bd), C(0x1e1e1e)], r(5));
    shirt = pick([C(0xd8d0bd), C(0xb8ab90), C(0x8e2418), C(0x2a2a2a)], r(6));
    legs = pick([C(0x3b3128), C(0x5c5040), C(0x283048), C(0x7a6a52), C(0xb0a488)], r(7));
    shin = r(8) < 0.5 ? 'bare' : r(8) < 0.75 ? 'boots' : 'shoes';
    hat = r(9) < 0.35 ? 'bandana' : r(9) < 0.55 ? 'tricorne' : r(9) < 0.7 ? 'knit' : null;
    hatA = hat === 'bandana' ? pick([C(0x8a1a12), C(0x1a2a5a), C(0x6a4a1a), C(0xd0c8b0)], r(10)) : hat === 'knit' ? pick([C(0x7a1812), C(0x3a3a3a)], r(10)) : C(0x16130f);
    trim = C(0x1a1612);
  }
  if (role === 'boy') head = 'short';
  // Sleeves follow the garment: fitted for coats and jackets, the shirt's
  // loose sleeves (sometimes rolled) under a waistcoat or on their own.
  const upper = torso === 'coat' || torso === 'jacket' ? 'fitted' : torso === 'bare' ? 'bare' : 'loose';
  const fore = torso === 'coat' || torso === 'jacket' ? 'cuff' : torso === 'bare' ? 'bare' : r(11) < 0.5 ? 'rolled' : 'loose';
  const shirtOfArms = torso === 'shirt' ? coat : shirt;
  const bodyA = torso === 'bare' ? legs : coat;
  const sleeveCap = fore === 'bare' || fore === 'rolled' ? skin : upper === 'loose' ? shirtOfArms : coat;
  return {
    torso, head, hat, upper, fore, thigh, shin,
    body: [bodyA, shirt, skin],
    arms: [coat, shirtOfArms, skin],
    legs: [legs, hose, skin],
    headC: [skin, hair, skin],
    hatC: [hatA, trim, skin],
    outfit: { coat: torso === 'bare' ? skin : torso === 'shirt' ? coat : coat, sleeve: sleeveCap, legs, hat: hat ? hatA : null },
  };
}

/** What he has in his right hand, if anything. */
function heldItem(m: CrewMember, p: Pose): string | null {
  if (p.musket || !m.alive || p.lying !== 0) return null;
  if (m.stance === 'fight' || m.task === 'board') return 'cutlass';
  if (m.task === 'gun' && m.stance === 'work') return 'rammer';
  if (m.task === 'fire' && (m.stance === 'work' || m.stance === 'run')) return 'bucket';
  if (m.task === 'repair' && m.stance === 'work') return 'mallet';
  if (m.task === 'lookout' || (m.role === 'officer' && m.stance === 'stand' && m.id % 3 === 0)) return 'spyglass';
  return null;
}

function capsule(r: number, len: number, down = true): THREE.BufferGeometry {
  const g = new THREE.CapsuleGeometry(r, len, 3, 8);
  g.translate(0, down ? -(len / 2 + r * 0.6) : len / 2 + r * 0.6, 0);
  return g;
}

function partGeometry(name: PartName): THREE.BufferGeometry {
  switch (name) {
    case 'torso': {
      const g = new THREE.CapsuleGeometry(0.17, 0.34, 4, 10);
      g.scale(1, 1, 0.72);
      g.translate(0, 0.3, 0);
      return g;
    }
    case 'head': {
      const g = new THREE.SphereGeometry(0.105, 12, 9);
      g.scale(0.95, 1.1, 0.9);
      g.translate(0, 0.14, 0);
      return g;
    }
    case 'hat': {
      const brim = new THREE.CylinderGeometry(0.17, 0.17, 0.02, 12);
      brim.translate(0, 0.235, 0);
      const crown = new THREE.CylinderGeometry(0.09, 0.105, 0.1, 12);
      crown.translate(0, 0.29, 0);
      const g = new THREE.BufferGeometry();
      const merged = [brim, crown].map((x) => x.toNonIndexed());
      const pos = merged.flatMap((x) => Array.from((x.getAttribute('position') as THREE.BufferAttribute).array));
      const nrm = merged.flatMap((x) => Array.from((x.getAttribute('normal') as THREE.BufferAttribute).array));
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
      return g;
    }
    case 'upperL': case 'upperR': return capsule(0.052, 0.22);
    case 'foreL': case 'foreR': return capsule(0.045, 0.22);
    case 'thighL': case 'thighR': return capsule(0.075, 0.32);
    case 'shinL': case 'shinR': {
      const g = capsule(0.058, 0.36);
      return g;
    }
    case 'musket': {
      const g = new THREE.CylinderGeometry(0.018, 0.024, 1.45, 6);
      g.rotateZ(-Math.PI / 2);
      g.translate(0.45, 0, 0);
      return g;
    }
  }
}

const PARTS: PartName[] = ['torso', 'head', 'hat', 'upperL', 'upperR', 'foreL', 'foreR', 'thighL', 'thighR', 'shinL', 'shinR', 'musket'];

const tmpM = new THREE.Matrix4();
const tmpM2 = new THREE.Matrix4();
const tmpQ = new THREE.Quaternion();
const tmpE = new THREE.Euler();
const tmpV = new THREE.Vector3();
const ONE = new THREE.Vector3(1, 1, 1);

function rot(out: THREE.Matrix4, x: number, y: number, z: number): THREE.Matrix4 {
  tmpE.set(x, y, z, 'YZX');
  return out.makeRotationFromEuler(tmpE);
}


/** Model material names -> colour channel weights (A, B, C, fixed), fixed colour, roughness, metalness. */
const CHANNELS: Record<string, { w: [number, number, number, number]; c: [number, number, number]; rough: number; metal: number }> = {
  cloth: { w: [1, 0, 0, 0], c: [0, 0, 0], rough: 0.9, metal: 0 },
  cloth2: { w: [0, 1, 0, 0], c: [0, 0, 0], rough: 0.85, metal: 0 },
  hair: { w: [0, 1, 0, 0], c: [0, 0, 0], rough: 0.7, metal: 0 },
  skin: { w: [0, 0, 1, 0], c: [0, 0, 0], rough: 0.55, metal: 0 },
  leather: { w: [0, 0, 0, 1], c: [0.07, 0.04, 0.022], rough: 0.5, metal: 0 },
  metal: { w: [0, 0, 0, 1], c: [0.5, 0.42, 0.28], rough: 0.35, metal: 0.9 },
  wood: { w: [0, 0, 0, 1], c: [0.16, 0.09, 0.045], rough: 0.6, metal: 0 },
  dark: { w: [0, 0, 0, 1], c: [0.012, 0.009, 0.007], rough: 0.5, metal: 0 },
};

/** One segment cut from the model, merged into a single geometry with its channels baked per vertex. */
function detailGeometry(node: THREE.Object3D): THREE.BufferGeometry | null {
  const parts: THREE.BufferGeometry[] = [];
  for (const { key, geometry } of primitives(node)) {
    const ch = CHANNELS[key] ?? CHANNELS.cloth!;
    const g = new THREE.BufferGeometry();
    const pos = geometry.getAttribute('position');
    const n = pos.count;
    g.setAttribute('position', pos.clone());
    g.setAttribute('normal', geometry.getAttribute('normal').clone());
    if (geometry.index) g.setIndex(geometry.index.clone());
    const chan = new Float32Array(n * 4), col = new Float32Array(n * 3), surf = new Float32Array(n * 2), grime = new Float32Array(n * 4);
    const src = geometry.getAttribute('grime');
    for (let i = 0; i < n; i++) {
      chan.set(ch.w, i * 4);
      col.set(ch.c, i * 3);
      surf[i * 2] = ch.rough;
      surf[i * 2 + 1] = ch.metal;
      if (src) for (let k = 0; k < 4; k++) grime[i * 4 + k] = src.getComponent(i, k);
      else grime[i * 4 + 3] = 1;
    }
    g.setAttribute('chan', new THREE.BufferAttribute(chan, 4));
    g.setAttribute('fixedCol', new THREE.BufferAttribute(col, 3));
    g.setAttribute('surf', new THREE.BufferAttribute(surf, 2));
    g.setAttribute('grime', new THREE.BufferAttribute(grime, 4));
    parts.push(g);
  }
  if (!parts.length) return null;
  const merged = mergeGeometries(parts, false);
  merged?.computeBoundingSphere();
  return merged;
}

/** The detailed figures' material: colour from the channels, per-vertex surface, baked occlusion. */
function detailMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0, side: THREE.DoubleSide });
  return patchMaterial(mat, (shader) => {
    shader.vertexShader = `attribute vec4 chan;
attribute vec3 fixedCol;
attribute vec2 surf;
attribute vec4 grime;
attribute vec3 colA;
attribute vec3 colB;
attribute vec3 colC;
varying vec3 vAlb;
varying vec2 vSurf;
varying vec2 vGrimeC;
` + shader.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
      vAlb = chan.x * colA + chan.y * colB + chan.z * colC + chan.w * fixedCol;
      vSurf = surf;
      vGrimeC = grime.xy;`);
    shader.fragmentShader = 'varying vec3 vAlb;\nvarying vec2 vSurf;\nvarying vec2 vGrimeC;\n' + shader.fragmentShader
      .replace('#include <color_fragment>', `#include <color_fragment>
      diffuseColor.rgb *= vAlb * (1.0 - vGrimeC.y * 0.25);`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
      roughnessFactor = vSurf.x;`)
      .replace('#include <metalnessmap_fragment>', `#include <metalnessmap_fragment>
      metalnessFactor = vSurf.y;`)
      .replace('#include <aomap_fragment>', `#include <aomap_fragment>
      reflectedLight.indirectDiffuse *= 1.0 - vGrimeC.x * 0.7;
      reflectedLight.indirectSpecular *= 1.0 - vGrimeC.x * 0.6;`);
  }, 'crewDetail');
}

/** One segment cut, instanced for every man wearing it. */
class DetailPart {
  readonly mesh: THREE.InstancedMesh;
  private readonly a: THREE.InstancedBufferAttribute;
  private readonly b: THREE.InstancedBufferAttribute;
  private readonly c: THREE.InstancedBufferAttribute;
  count = 0;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material) {
    const mk = () => {
      const at = new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3);
      at.setUsage(THREE.DynamicDrawUsage);
      return at;
    };
    this.a = mk();
    this.b = mk();
    this.c = mk();
    geometry.setAttribute('colA', this.a);
    geometry.setAttribute('colB', this.b);
    geometry.setAttribute('colC', this.c);
    this.mesh = new THREE.InstancedMesh(geometry, material, CAPACITY);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
  }

  put(m: THREE.Matrix4, cols: readonly [THREE.Color, THREE.Color, THREE.Color]): void {
    const i = this.count;
    if (i >= CAPACITY) return;
    this.mesh.setMatrixAt(i, m);
    this.a.setXYZ(i, cols[0].r, cols[0].g, cols[0].b);
    this.b.setXYZ(i, cols[1].r, cols[1].g, cols[1].b);
    this.c.setXYZ(i, cols[2].r, cols[2].g, cols[2].b);
    this.count = i + 1;
  }

  flush(): void {
    commitInstances(this.mesh, this.count, [this.a, this.b, this.c]);
  }
}

const NO_COLS: [THREE.Color, THREE.Color, THREE.Color] = [new THREE.Color(1, 1, 1), new THREE.Color(0.55, 0.5, 0.42), new THREE.Color(1, 1, 1)];

export class CrewView {
  readonly group = new THREE.Group();
  private readonly meshes: Record<PartName, THREE.InstancedMesh>;
  private readonly counts: Record<PartName, number>;
  private readonly mBase = new THREE.Matrix4();
  /** The Blender-built segments by '<part>_<cut>' (empty without the models). */
  private readonly detail = new Map<string, DetailPart>();

  constructor(art: ArtSet | null = null) {
    const mat = patchMaterial(new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0 }), undefined, 'crew');
    const gunMat = patchMaterial(new THREE.MeshStandardMaterial({ color: 0x2a2018, roughness: 0.6, metalness: 0.3 }), undefined, 'musket');
    this.meshes = {} as Record<PartName, THREE.InstancedMesh>;
    this.counts = {} as Record<PartName, number>;
    for (const p of PARTS) {
      const im = new THREE.InstancedMesh(partGeometry(p), p === 'musket' ? gunMat : mat, CAPACITY);
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      if (p !== 'musket') {
        im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3);
        im.instanceColor.setUsage(THREE.DynamicDrawUsage);
      }
      im.castShadow = true;
      im.receiveShadow = true;
      im.frustumCulled = false;
      im.count = 0;
      this.meshes[p] = im;
      this.counts[p] = 0;
      this.group.add(im);
    }
    if (art) {
      const dm = detailMaterial();
      for (const node of art.nodes) {
        const g = detailGeometry(node);
        if (!g) continue;
        const d = new DetailPart(g, dm);
        this.detail.set(node.name, d);
        this.group.add(d.mesh);
      }
    }
  }

  /**
   * Pose every visible man. `shipMatrix` maps each vessel's ship-local frame
   * (unscaled metres) to the world.
   */
  /** `sees`: is a world-space sphere in frame (men in the water out of it aren't drawn)? */
  update(vessels: readonly Vessel[], shipMatrix: (v: Vessel) => THREE.Matrix4 | null, cam: THREE.Vector3, time: number, sees?: (x: number, y: number, z: number, r: number) => boolean): void {
    for (const p of PARTS) this.counts[p] = 0;
    for (const d of this.detail.values()) d.count = 0;
    const base = this.mBase;
    for (const v of vessels) {
      const sm = shipMatrix(v);
      const s = v.ship;
      const near = Math.hypot(s.com.x - cam.x, s.com.z - cam.z) < 750;
      for (const m of v.crew.members) {
        if (!m.onBoard) {
          // In the water: world space.
          if (Math.hypot(m.wx - cam.x, m.wz - cam.z) > 500) continue;
          if (sees && !sees(m.wx, m.wy, m.wz, 2)) continue;
          base.compose(tmpV.set(m.wx, m.wy, m.wz), tmpQ.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, -m.facing), ONE);
          this.pose(base, m, v.faction, time, true, cam);
          continue;
        }
        if (!near || !sm || s.sunk) continue;
        if (!m.alive && m.stance !== 'dead') continue;
        const y = (walkHeight(m.x, m.z) + m.alt) * s.sy;
        tmpM2.compose(tmpV.set(m.x * s.sx, y, m.z * s.sz), tmpQ.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, m.facing), ONE);
        base.multiplyMatrices(sm, tmpM2);
        this.pose(base, m, v.faction, time, false, cam);
      }
    }
    for (const p of PARTS) commitInstances(this.meshes[p], this.counts[p]);
    for (const d of this.detail.values()) d.flush();
  }

  private put(part: PartName, m: THREE.Matrix4, color: THREE.Color | null): void {
    const i = this.counts[part];
    if (i >= CAPACITY) return;
    const im = this.meshes[part];
    im.setMatrixAt(i, m);
    if (color && im.instanceColor) im.setColorAt(i, color);
    this.counts[part] = i + 1;
  }

  /** Place a detailed segment; false if that cut isn't in the models. */
  private putDetail(key: string, m: THREE.Matrix4, cols: readonly [THREE.Color, THREE.Color, THREE.Color]): boolean {
    const d = this.detail.get(key);
    if (!d) return false;
    d.put(m, cols);
    return true;
  }

  private readonly looks = new WeakMap<CrewMember, Dress>();
  private readonly mBody = new THREE.Matrix4();
  private readonly mPelvis = new THREE.Matrix4();
  private readonly mTorso = new THREE.Matrix4();
  private readonly mNeck = new THREE.Matrix4();
  private readonly mUpper = new THREE.Matrix4();
  private readonly mLower = new THREE.Matrix4();
  private readonly camLocal = new THREE.Vector3();

  private pose(base: THREE.Matrix4, m: CrewMember, faction: Faction, time: number, inWater: boolean, cam: THREE.Vector3): void {
    let look = this.looks.get(m);
    if (!look) {
      const skin = SKIN[Math.floor(m.appearance * 97) % SKIN.length]!;
      look = dress(faction === 'player' ? 'pirate' : faction, m.role, m.appearance, skin);
      this.looks.set(m, look);
    }
    const o = look.outfit;
    const skin = look.body[2];
    const detailed = this.detail.size > 0 && this.camLocal.setFromMatrixPosition(base).distanceToSquared(cam) < DETAIL_RANGE * DETAIL_RANGE;
    const p = stancePose(m.stance, m.anim + m.id * 0.37, m.id, time, m.health);
    const body = this.mBody;
    if (p.lying !== 0) {
      // Lying on the deck (or face down in the water).
      const r = p.lying === 1 ? rot(tmpM, 0, 0, -Math.PI / 2) : p.lying === -1 ? rot(tmpM, 0, 0, Math.PI / 2) : rot(tmpM, Math.PI / 2, 0, 0);
      body.multiplyMatrices(base, tmpM2.makeTranslation(inWater ? 0 : -0.1, inWater ? 0 : 0.14, 0)).multiply(r);
      body.multiply(tmpM2.makeTranslation(0, -0.95, 0));
    } else body.copy(base);
    // Pelvis, torso and head.
    const pelvis = this.mPelvis.copy(body).multiply(tmpM2.makeTranslation(0, p.hip, 0));
    const torso = this.mTorso.copy(pelvis).multiply(rot(tmpM, 0, 0, -p.lean));
    const neck = this.mNeck.copy(torso).multiply(tmpM2.makeTranslation(0, 0.62, 0));
    if (detailed) {
      this.putDetail(`torso_${look.torso}`, torso, look.body);
      this.putDetail(`head_${look.head}`, neck, look.headC);
      if (look.hat) this.putDetail(`hat_${look.hat}`, neck, look.hatC);
    } else {
      this.put('torso', torso, o.coat);
      this.put('head', neck, skin);
      if (o.hat) this.put('hat', neck, o.hat);
    }
    const item = detailed ? heldItem(m, p) : null;
    for (let k = 0; k < 2; k++) {
      const side = k === 0 ? 1 : -1;
      const arm = k === 0 ? p.armR : p.armL;
      const up = this.mUpper.copy(torso).multiply(tmpM2.makeTranslation(0, 0.52, side * 0.215)).multiply(rot(tmpM, -side * arm[1], 0, arm[0]));
      const el = this.mLower.copy(up).multiply(tmpM2.makeTranslation(0, -0.31, 0)).multiply(rot(tmpM, 0, 0, arm[2]));
      if (detailed) {
        this.putDetail(`upper_${look.upper}`, up, look.arms);
        this.putDetail(`fore${k === 0 ? 'R' : 'L'}_${look.fore}`, el, look.arms);
        if (k === 0 && item) this.putDetail(`item_${item}`, el, NO_COLS);
      } else {
        this.put(k === 0 ? 'upperR' : 'upperL', up, o.coat);
        this.put(k === 0 ? 'foreR' : 'foreL', el, o.sleeve ?? o.coat);
      }
    }
    for (let k = 0; k < 2; k++) {
      const side = k === 0 ? 1 : -1;
      const leg = k === 0 ? p.legR : p.legL;
      const hip = this.mUpper.copy(pelvis).multiply(tmpM2.makeTranslation(0, 0, side * 0.1)).multiply(rot(tmpM, 0, 0, leg[0]));
      const knee = this.mLower.copy(hip).multiply(tmpM2.makeTranslation(0, -0.45, 0)).multiply(rot(tmpM, 0, 0, leg[1]));
      if (detailed) {
        this.putDetail(`thigh_${look.thigh}`, hip, look.legs);
        this.putDetail(`shin_${look.shin}`, knee, look.legs);
      } else {
        this.put(k === 0 ? 'thighR' : 'thighL', hip, o.legs);
        this.put(k === 0 ? 'shinR' : 'shinL', knee, o.legs);
      }
    }
    if (p.musket) {
      const mm = this.mUpper.copy(torso).multiply(tmpM2.makeTranslation(0.25, 0.47, 0.12));
      if (!(detailed && this.putDetail('musket', mm, NO_COLS))) this.put('musket', mm, null);
    }
  }
}

/** Joint angles for a stance at animation phase t. */
function stancePose(stance: Stance, t: number, id: number, time: number, health: number): Pose {
  const P: Pose = { hip: 0.95, lean: 0.03, armL: [0.05, 0.12, 0.2], armR: [0.05, 0.12, 0.2], legL: [0, 0], legR: [0, 0], lying: 0, musket: false };
  const idle = Math.sin(time * 0.8 + id) * 0.04;
  switch (stance) {
    case 'stand':
      P.armL[0] = idle;
      P.armR[0] = -idle;
      P.lean = 0.02 + idle * 0.3;
      break;
    case 'walk': case 'run': {
      const run = stance === 'run';
      const ph = t * (run ? 9.5 : 6.2);
      const s = Math.sin(ph), c = Math.cos(ph);
      const a = run ? 0.75 : 0.42;
      P.legR = [a * s, -Math.max(0, -c) * (run ? 1.4 : 0.8) - 0.05];
      P.legL = [-a * s, -Math.max(0, c) * (run ? 1.4 : 0.8) - 0.05];
      P.armR = [-a * 0.9 * s, 0.12, run ? 1.3 : 0.35];
      P.armL = [a * 0.9 * s, 0.12, run ? 1.3 : 0.35];
      P.lean = run ? 0.28 : 0.06;
      P.hip = 0.95 - Math.abs(c) * (run ? 0.06 : 0.03);
      break;
    }
    case 'work': {
      // Heaving on a handspike, ramming, pumping, swinging buckets.
      const ph = t * 4.2;
      const s = Math.sin(ph);
      P.lean = 0.45 + s * 0.12;
      P.armL = [1.0 + s * 0.4, 0.15, 0.7 - s * 0.3];
      P.armR = [1.1 + s * 0.45, 0.1, 0.6 - s * 0.3];
      P.legL = [0.35, -0.5];
      P.legR = [-0.15, -0.15];
      P.hip = 0.86;
      break;
    }
    case 'haul': {
      // Hand over hand on a fall, leaning back against it.
      const ph = t * 3.1;
      const s = Math.sin(ph);
      P.lean = -0.28;
      P.armR = [1.45 + s * 0.35, 0.05, 0.3 + Math.max(0, s) * 0.8];
      P.armL = [1.45 - s * 0.35, 0.05, 0.3 + Math.max(0, -s) * 0.8];
      P.legL = [0.45, -0.3];
      P.legR = [-0.3, -0.1];
      P.hip = 0.88;
      break;
    }
    case 'crouch':
      P.hip = 0.52;
      P.lean = 0.55;
      P.legL = [1.5, -2.1];
      P.legR = [1.1, -1.9];
      P.armL = [0.75, 0.2, 1.0];
      P.armR = [0.9, 0.2, 1.1];
      break;
    case 'aim':
      P.lean = 0.12;
      P.armR = [1.35, 0.35, 0.55];
      P.armL = [1.5, -0.1, 0.25];
      P.legL = [0.3, -0.1];
      P.legR = [-0.25, -0.05];
      P.musket = true;
      break;
    case 'fight': {
      const ph = t * 7.5 + id;
      const s = Math.sin(ph);
      P.lean = 0.25 + s * 0.1;
      P.armR = [1.1 + s * 1.2, 0.3, 0.3 + Math.max(0, s) * 0.8];
      P.armL = [0.5, 0.35, 1.2];
      P.legL = [0.55, -0.4];
      P.legR = [-0.35, -0.15];
      P.hip = 0.88;
      break;
    }
    case 'hands':
      P.armL = [2.9, 0.35, 0.2];
      P.armR = [2.9, 0.35, 0.2];
      P.lean = -0.05;
      break;
    case 'climb': {
      const ph = t * 3.6;
      const s = Math.sin(ph);
      P.armR = [2.5 + s * 0.4, 0.25, 0.6 - s * 0.3];
      P.armL = [2.5 - s * 0.4, 0.25, 0.6 + s * 0.3];
      P.legR = [0.6 + s * 0.5, -0.9 - s * 0.4];
      P.legL = [0.6 - s * 0.5, -0.9 + s * 0.4];
      P.lean = 0.12;
      break;
    }
    case 'swim': {
      const ph = t * 2.4;
      P.lying = 1;
      P.armR = [ph % (Math.PI * 2), 0.2, 0.4];
      P.armL = [(ph + Math.PI) % (Math.PI * 2), 0.2, 0.4];
      P.legL = [Math.sin(ph * 2) * 0.3, -0.2];
      P.legR = [-Math.sin(ph * 2) * 0.3, -0.2];
      P.lean = 0;
      break;
    }
    case 'down': {
      // Wounded: curled on his side, stirring now and then.
      const s = Math.sin(time * 1.3 + id) * 0.15 * health;
      P.lying = 2;
      P.lean = 0.5;
      P.armL = [1.2 + s, 0.2, 1.6];
      P.armR = [0.9 - s, 0.1, 1.4];
      P.legL = [1.0, -1.3];
      P.legR = [0.7 + s, -1.0];
      break;
    }
    case 'dead':
      P.lying = -1;
      P.lean = 0;
      P.armL = [0.4 + (id % 3) * 0.5, 0.6, 0.3];
      P.armR = [(id % 5) * 0.3, 0.9, 0.2];
      P.legL = [0.15, -0.1];
      P.legR = [-0.1, -0.35];
      break;
  }
  return P;
}
